import React, { useEffect, useRef, useState } from "react"
import { View, Image, StyleSheet, Animated, Easing, Text } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import { useIsFocusedM } from "@common/hooks/useIsFocusedM"
import {
    championBowTieForTutorSkillSetId,
    championColorForTutorSkillSetId,
} from "@common/components/avatar/AvatarM"

/** Le lavande des scènes — celui de la landing page d'où vient le parent. */
export const SCENE_BACKGROUND = "#F3F5FF"

/**
 * La scène visuelle d'une étape de funnel (stage, Rentrée) : un aplat lavande et des
 * calques posés dessus — un élève, un appareil, un objet 3D, un champion.
 *
 * Chaque calque est positionné en FRACTION de la scène (0–1), avec un cadrage
 * par variante (`mobile` : le bandeau en tête d'écran ; `desktop` : le panneau
 * de gauche). Le même asset sert aux deux ; seule sa place change. Les scènes
 * elles-mêmes vivent dans {@see FUNNEL_SCENES}.
 *
 * La scène se mesure (`onLayout`) et convertit elle-même les fractions en
 * pixels : la hauteur d'un calque découle de sa largeur et du ratio du fichier.
 * On ne s'appuie ni sur `aspectRatio` ni sur les pourcentages d'un
 * positionnement absolu — les deux se comportent différemment entre web et
 * natif, et un calque sans hauteur est un calque invisible.
 *
 * Un calque `round` est un visage : `source` est alors `{ uri }`, rendu en
 * cercle cerclé de blanc. Les calques marqués `float` respirent : une montée de quelques pixels, lente,
 * décalée d'un calque à l'autre pour que rien ne bouge en bloc. C'est le seul
 * mouvement, et il ne vient pas de l'asset — l'image reste une image.
 *
 * Une scène peut porter une `caption` : un bandeau de texte que le panneau
 * desktop pose sous la scène ({@see FunnelSceneCaption}) — le bandeau mobile
 * n'a pas la hauteur de le lire.
 */
const FunnelScene = ({ scene, variant, style }) => {
    const [size, setSize] = useState(null)
    return (
        <View
            style={[styles.stage, style]}
            onLayout={(e) => {
                const { width, height } = e.nativeEvent.layout
                if (width && height) setSize({ width, height })
            }}
        >
            {size
                ? scene.layers.map((layer, index) => (
                      <Layer
                          key={layer.key ?? index}
                          index={index}
                          layer={layer}
                          frame={layer[variant]}
                          size={size}
                      />
                  ))
                : null}
        </View>
    )
}

/**
 * Chaque calque entre en fondu, l'un après l'autre : une scène se compose sous
 * les yeux, elle ne s'affiche pas d'un bloc — et un calque qui arrive en cours
 * d'écran (un visage servi par l'API) se pose de la même façon.
 */
const useEnter = (index) => {
    const enter = useRef(new Animated.Value(0)).current
    useEffect(() => {
        Animated.timing(enter, {
            toValue: 1,
            duration: 520,
            delay: 80 + index * 90,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: true,
        }).start()
    }, [enter, index])
    return enter
}

/** Le bandeau de texte d'une scène ; il entre après son dernier calque. */
export const FunnelSceneCaption = ({ scene, style }) => {
    const enter = useEnter(scene.layers.length)
    return (
        <Animated.View style={[styles.caption, style, { opacity: enter }]}>
            {scene.caption.map((item, i) => (
                <React.Fragment key={item.title}>
                    {i > 0 ? <View style={styles.captionDivider} /> : null}
                    <View style={styles.captionItem}>
                        <FontAwesome5
                            name={item.icon}
                            size={24}
                            color={theme.colors.primary}
                        />
                        <View style={styles.captionText}>
                            <Text style={styles.captionTitle}>
                                {item.title}
                            </Text>
                            <Text style={styles.captionSubtitle}>
                                {item.subtitle}
                            </Text>
                        </View>
                    </View>
                </React.Fragment>
            ))}
        </Animated.View>
    )
}

const Layer = ({ layer, frame, size, index = 0 }) => {
    const lift = useRef(new Animated.Value(0)).current
    const enter = useEnter(index)
    // Sur le web le pilote natif n'existe pas : la boucle tourne en JS à chaque
    // image. Les écrans précédents restent montés dans la pile — sans cette
    // garde, chaque étape franchie laisserait ses calques respirer derrière.
    const focused = useIsFocusedM()

    useEffect(() => {
        if (!layer.float || !focused) return undefined
        const loop = Animated.loop(
            Animated.sequence([
                Animated.delay(layer.delay ?? 0),
                Animated.timing(lift, {
                    toValue: -6,
                    duration: 1700,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
                Animated.timing(lift, {
                    toValue: 0,
                    duration: 1700,
                    easing: Easing.inOut(Easing.sin),
                    useNativeDriver: true,
                }),
            ])
        )
        loop.start()
        return () => loop.stop()
    }, [layer.float, layer.delay, focused])

    if (!frame) return null

    const width = frame.width * size.width
    const height = width / layer.ratio
    const px = (fraction, total) =>
        fraction == null ? undefined : fraction * total

    const face = layer.round
    return (
        <Animated.View
            style={[
                styles.layer,
                {
                    width,
                    height,
                    left: px(frame.left, size.width),
                    right: px(frame.right, size.width),
                    top: px(frame.top, size.height),
                    bottom: px(frame.bottom, size.height),
                    zIndex: layer.chosen ? 3 : (layer.zIndex ?? 1),
                    opacity: enter,
                    transform: [
                        { translateY: lift },
                        { rotate: `${layer.rotate ?? 0}deg` },
                        {
                            scale: enter.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0.92, 1],
                            }),
                        },
                    ],
                },
            ]}
        >
            <Image
                source={layer.source}
                resizeMode={face ? "cover" : "contain"}
                style={[
                    { width, height },
                    // Un visage : rond, cerclé de blanc — et, quand il est
                    // retenu, du MÊME anneau que sa carte (`AvatarM` : la
                    // couleur du champion du masteur). La photo est prise telle
                    // quelle, le cercle absorbe les cadrages imparfaits.
                    face && {
                        borderRadius: width / 2,
                        borderWidth: 3,
                        borderColor: layer.chosen
                            ? championColorForTutorSkillSetId(layer.skillSetId)
                            : "#FFFFFF",
                    },
                ]}
            />
            {face && layer.tie ? (
                // Le nœud papillon des masteurs — la signature Masteur, posé
                // exactement comme sur leur carte (`AvatarM`) : même asset,
                // même taille relative, même inclinaison à 45°, en bas au centre.
                <Image
                    source={championBowTieForTutorSkillSetId(layer.skillSetId)}
                    resizeMode="contain"
                    style={{
                        position: "absolute",
                        width: width * 0.46,
                        height: width * 0.46,
                        left: width * 0.27,
                        bottom: -width / 5,
                        transform: [{ rotateZ: "45deg" }],
                    }}
                />
            ) : null}
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    stage: {
        backgroundColor: SCENE_BACKGROUND,
        overflow: "hidden",
        position: "relative",
    },
    layer: { position: "absolute" },
    caption: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-around",
        paddingVertical: 16,
        paddingHorizontal: 12,
        borderRadius: 40,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
    },
    captionItem: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
    },
    captionText: { marginLeft: 10, flexShrink: 1 },
    captionDivider: {
        width: 1,
        alignSelf: "stretch",
        backgroundColor: theme.colors.lightMore,
        marginHorizontal: 8,
    },
    captionTitle: {
        fontFamily: "Montserrat-Bold",
        fontSize: 15,
        color: theme.colors.primary,
    },
    captionSubtitle: {
        fontFamily: "Montserrat-Regular",
        fontSize: 13,
        color: theme.colors.grey,
        marginTop: 2,
    },
})

export default FunnelScene
